import type { LocalChannel } from '../../db/db'
import type { Message } from '../../api/types'
import { Avatar } from '../../components/Avatar'
import { channelDisplayName, userName, type UserMap } from '../../lib/channels'
import { formatHitTime } from '../../lib/format'
import { describeClip, firstClip } from '../../lib/media'
import { groupByChannel } from './localSearch'
import { Snippet } from './Snippet'

export interface ResultItem {
  channelId: string
  message: Message
  snippet: string
}

interface Props {
  items: ResultItem[]
  channels: Map<string, LocalChannel>
  users: UserMap
  meId: string
  onOpen: (item: ResultItem) => void
}

/**
 * A hit's words around what matched. A clip with no words is described instead ("🎤 Voice message (0:42)"), with the file
 * name that matched for a video or an audio file.
 */
function HitText({ item }: { item: ResultItem }) {
  const clip = item.message.body ? undefined : firstClip(item.message.attachments)
  if (!clip) return <Snippet text={item.snippet} />
  if (clip.voice || !item.snippet) return <>{describeClip(clip)}</>
  return (
    <>
      {clip.kind === 'audio' ? '🎵 ' : `${describeClip(clip)} · `}
      <Snippet text={item.snippet} />
    </>
  )
}

/** A direct message or a bot's: the group's name is who the other side is. */
function oneToOne(channel: LocalChannel | undefined): boolean {
  return channel?.type === 'dm' || channel?.type === 'bot_dm'
}

export function SearchResultGroups({ items, channels, users, meId, onOpen }: Props) {
  return (
    <>
      {groupByChannel(items).map((group) => {
        const channel = channels.get(group.channelId)
        const name = channel ? channelDisplayName(channel, users, meId) : 'Other channel'
        return (
          <section key={group.channelId} className="result-group" aria-label={name}>
            <div className="result-group-title">
              <Avatar name={name} id={group.channelId} variant={channel?.type === 'channel' ? 'channel' : 'person'} />
              {name}
            </div>
            {group.items.map((item) => {
              // In a direct message the group already says who: their hits don't name them again; yours say You.
              const named = !(oneToOne(channel) && item.message.authorId !== meId)
              return (
                <button key={item.message.id} type="button" className="search-hit" onClick={() => onOpen(item)}>
                  {named ? <Avatar user={users.get(item.message.authorId)} id={item.message.authorId} /> : null}
                  <span className="hit-copy">
                    <span className="hit-meta">
                      {named ? <strong>{item.message.authorId === meId ? 'You' : userName(users, item.message.authorId)}</strong> : null}
                      <time dateTime={item.message.createdAt}>{formatHitTime(item.message.createdAt)}</time>
                      {item.message.forwarded ? <span className="hit-forwarded">Forwarded</span> : null}
                    </span>
                    <p>
                      <HitText item={item} />
                    </p>
                  </span>
                </button>
              )
            })}
          </section>
        )
      })}
    </>
  )
}
